import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight, MapPin } from "lucide-react";
import { listGymEvents, listMatches, listGenericEvents } from "../lib/db";

// tipo: "calcio" | "palestra" | "eventi" | "entrambi"
// compact: true = versione ridotta per la Home
export default function CalendarioWidget({ tipo = "entrambi", compact = false }) {
  const [gymEvents, setGymEvents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [genericEvents, setGenericEvents] = useState([]);
  const [tab, setTab] = useState(tipo === "palestra" ? "palestra" : tipo === "eventi" ? "eventi" : "calcio");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetches = [];
    if (tipo === "calcio" || tipo === "entrambi") {
      fetches.push(listMatches().then(setMatches).catch(() => {}));
    }
    if (tipo === "palestra" || tipo === "entrambi") {
      fetches.push(listGymEvents().then(setGymEvents).catch(() => {}));
    }
    if (tipo === "eventi" || tipo === "entrambi") {
      fetches.push(listGenericEvents().then(setGenericEvents).catch(() => {}));
    }
    Promise.all(fetches).finally(() => setLoading(false));
  }, [tipo]);

  // Mostra solo le gare programmate che non sono ancora passate.
  // listMatches() le restituisce già ordinate per data.
  const matchTodayStr = new Date().toISOString().split("T")[0];
  const upcoming = matches
    .filter((m) => m.status === "scheduled" && m.date >= matchTodayStr)
    .slice(0, compact ? 3 : undefined);

  const eventsToShow = compact ? gymEvents.slice(0, 3) : gymEvents;

  const todayStr = new Date().toISOString().split("T")[0];
  const upcomingGenericEvents = genericEvents
    .filter((e) => (e.event_date_end || e.event_date) >= todayStr)
    .slice(0, compact ? 3 : undefined);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 rounded-xl bg-zinc-800 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Tab selector — solo se tipo === "entrambi" */}
      {tipo === "entrambi" && (
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setTab("calcio")}
            className={`px-5 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${
              tab === "calcio"
                ? "bg-yellow-400 text-zinc-950"
                : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}
          >
            ⚽ Calcio
          </button>
          <button
            onClick={() => setTab("palestra")}
            className={`px-5 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${
              tab === "palestra"
                ? "bg-yellow-400 text-zinc-950"
                : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}
          >
            🏋️ Palestra
          </button>
          <button
            onClick={() => setTab("eventi")}
            className={`px-5 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${
              tab === "eventi"
                ? "bg-yellow-400 text-zinc-950"
                : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}
          >
            📅 Eventi
          </button>
        </div>
      )}

      {/* CALCIO */}
      {(tipo === "calcio" || (tipo === "entrambi" && tab === "calcio")) && (
        <div className="space-y-3">
          {upcoming.length === 0 && (
            <p className="text-zinc-500 text-sm">Nessuna gara in programma.</p>
          )}
          {upcoming.map((m) => (
            <Link
              to="/calendario"
              key={m.id}
              className="rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-yellow-400/40 transition-colors p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer"
            >
              <div className="flex items-center gap-3 sm:w-44">
                <div className="h-11 w-11 rounded-lg bg-zinc-950 border border-yellow-400/30 flex flex-col items-center justify-center font-display shrink-0">
                  <span className="text-[10px] text-yellow-400 font-bold leading-none">
                    {new Date(m.date).toLocaleDateString("it-IT", { month: "short", timeZone: "Europe/Rome" }).toUpperCase()}
                  </span>
                  <span className="text-base font-black leading-none mt-0.5">
                    {new Date(m.date).toLocaleDateString("it-IT", { day: "numeric", timeZone: "Europe/Rome" })}
                  </span>
                </div>
                <div>
                 <div className="text-xs text-zinc-500">
                   {new Date(m.date).toLocaleDateString("it-IT", { weekday: "long", timeZone: "Europe/Rome" })} • ore {new Date(m.date).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
                 </div>
                  {m.venue && (
                    <div className="text-xs text-zinc-400 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />{m.venue}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center gap-3">
                {m.title ? (
                  <div className="font-display font-bold text-sm sm:text-base text-center flex-1">{m.title}</div>
                ) : (
                  <>
                    <div className="font-display font-bold text-sm sm:text-base text-right flex-1">{m.home}</div>
                    <div className="text-yellow-400 font-display font-black text-lg">VS</div>
                    <div className="font-display font-bold text-sm sm:text-base text-left flex-1">{m.away}</div>
                  </>
                )}
              </div>
              <div className="text-xs text-zinc-500 sm:text-right sm:w-36 shrink-0">{m.competition}</div>
            </Link>
          ))}
          {compact && upcoming.length > 0 && (
            <Link
              to="/calendario"
              className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-yellow-400 hover:gap-3 transition-all mt-2"
            >
              Calendario completo <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}

      {/* PALESTRA */}
      {(tipo === "palestra" || (tipo === "entrambi" && tab === "palestra")) && (
        <div className="space-y-3">
          {eventsToShow.length === 0 && (
            <p className="text-zinc-500 text-sm">Nessun corso in programma.</p>
          )}
          {eventsToShow.map((e) => (
            <Link
              to="/calendario"
              key={e.id}
              className="rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-yellow-400/40 transition-colors p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer"
            >
              <div className="sm:w-28 shrink-0">
                <div className="font-display font-bold text-yellow-400 text-sm uppercase tracking-wide">
                  {e.day_of_week}
                </div>
                <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
                  <Clock className="h-3 w-3" />
                  {e.time_start} – {e.time_end}
                </div>
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-sm sm:text-base">{e.course_name}</div>
              </div>
              <div className="flex items-center gap-1 text-xs text-zinc-500 sm:w-36 shrink-0">
                <User className="h-3 w-3" />{e.instructor}
              </div>
            </Link>
          ))}
          {compact && eventsToShow.length > 0 && (
            <Link
              to="/calendario"
              className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-yellow-400 hover:gap-3 transition-all mt-2"
            >
              Orari completi <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}

      {/* EVENTI GENERICI */}
      {(tipo === "eventi" || (tipo === "entrambi" && tab === "eventi")) && (
        <div className="space-y-3">
          {upcomingGenericEvents.length === 0 && (
            <p className="text-zinc-500 text-sm">Nessun evento in programma.</p>
          )}
          {upcomingGenericEvents.map((e) => {
            const isMultiDay = e.event_date_end && e.event_date_end !== e.event_date;
            return (
              <Link
                to="/calendario"
                key={e.id}
                className="rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-yellow-400/40 transition-colors p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer"
              >
                <div className="flex items-center gap-3 sm:w-44">
                  <div className="h-11 w-11 rounded-lg bg-zinc-950 border border-yellow-400/30 flex flex-col items-center justify-center font-display shrink-0">
                    <span className="text-[10px] text-yellow-400 font-bold leading-none">
                      {new Date(e.event_date + "T00:00:00").toLocaleDateString("it-IT", { month: "short", timeZone: "Europe/Rome" }).toUpperCase()}
                    </span>
                    <span className="text-base font-black leading-none mt-0.5">
                      {new Date(e.event_date + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", timeZone: "Europe/Rome" })}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-500">
                      {isMultiDay
                        ? `fino al ${new Date(e.event_date_end + "T00:00:00").toLocaleDateString("it-IT", { day: "numeric", month: "long", timeZone: "Europe/Rome" })}`
                        : e.time_start
                        ? `ore ${e.time_start.slice(0, 5)}`
                        : new Date(e.event_date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", timeZone: "Europe/Rome" })}
                    </div>
                    {e.location && (
                      <div className="text-xs text-zinc-400 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />{e.location}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-sm sm:text-base">{e.title}</div>
                  {e.description && <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{e.description}</div>}
                </div>
              </Link>
            );
          })}
          {compact && upcomingGenericEvents.length > 0 && (
            <Link
              to="/calendario"
              className="inline-flex items-center gap-1.5 text-sm font-display font-bold text-yellow-400 hover:gap-3 transition-all mt-2"
            >
              Tutti gli eventi <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}